import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type { CartProductInterface, OrganisationShortType } from '@/types'
import { isClientUser, isSupplierStaff } from '@/utils/auth/userRoles'

import type { ValidationResult } from './cartServiceTypes'

export class CartValidationService {
  getAvailableQuantity(product: CartProductInterface): number {
    const stockItem = product.suppliersStock?.[0]?.stock?.[0]
    return stockItem?.quantity ?? 0
  }

  getQuantityInCart(product: CartProductInterface): number {
    const stockItem = product.suppliersStock?.[0]?.stock?.[0]
    return stockItem?.cartInfo?.quantity ?? stockItem?.quantityInCart ?? 0
  }

  isStatusUnavailable(product: CartProductInterface): boolean {
    return (
      product.availabilityStatus !== undefined &&
      product.availabilityStatus !== 'AVAILABLE'
    )
  }

  isProductAvailable(product: CartProductInterface): boolean {
    if (this.isStatusUnavailable(product)) return false

    const stockItem = product.suppliersStock?.[0]?.stock?.[0]
    const availableQuantity = this.getAvailableQuantity(product)
    const quantityInCart = this.getQuantityInCart(product)

    return !!(
      stockItem &&
      availableQuantity > 0 &&
      quantityInCart > 0 &&
      quantityInCart <= availableQuantity
    )
  }

  isProductProblematic(product: CartProductInterface): boolean {
    if (this.isStatusUnavailable(product)) return true

    const availableQuantity = this.getAvailableQuantity(product)
    const quantityInCart = this.getQuantityInCart(product)

    return (
      availableQuantity === 0 ||
      quantityInCart > availableQuantity ||
      quantityInCart === 0
    )
  }

  filterOrderableProducts(
    products: CartProductInterface[]
  ): CartProductInterface[] {
    return products.filter((product) => this.isProductAvailable(product))
  }

  filterAvailableProducts(
    products: CartProductInterface[]
  ): CartProductInterface[] {
    return products.filter((product) => this.isProductAvailable(product))
  }

  hasUnorderableProducts(products: CartProductInterface[]): boolean {
    return products.some((product) => !this.isProductAvailable(product))
  }

  private getContextState() {
    return useUserInfoStore.getState()
  }

  isOrganisationReady(): boolean {
    const { organisation } = this.getContextState()
    return Object.keys(organisation).length > 0
  }

  shouldShowWarning(): boolean {
    const { currentClient, tokenRoles } = this.getContextState()
    return isSupplierStaff(tokenRoles) && !currentClient?.id
  }

  getClientOrganisation(): OrganisationShortType | null {
    const { currentClient, userOrganisation, tokenRoles } =
      this.getContextState()

    if (currentClient?.id) return currentClient

    if (isClientUser(tokenRoles) && userOrganisation?.id) {
      return userOrganisation
    }

    return null
  }

  canCreateOrder(): ValidationResult {
    const { organisation, tokenRoles } = this.getContextState()

    if (!organisation || Object.keys(organisation).length === 0) {
      return {
        isValid: false,
        errorMessage: 'Данные организации не загружены',
      }
    }

    if (isClientUser(tokenRoles)) {
      return { isValid: true }
    }

    return { isValid: true }
  }

  hasClientContext(): boolean {
    const { currentClient, tokenRoles } = this.getContextState()
    return !!currentClient?.id || isClientUser(tokenRoles)
  }
}

export const cartValidationService = new CartValidationService()
